import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { AuthenticationError, handleServerFormError, trackError, ValidationError } from '#api/errors.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ErrorMessage, Input } from '#components/atoms/forms/index.ts';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '#components/patterns/modal/index.ts';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { createPendingAddAdmin } from './pendingAddAdmin.ts';
import { useAddAdminMutation } from './useAddAdminMutation.ts';

const FORM_NAME = 'add-admin-form';

type FormData = {
  email: string;
};

type Props = {
  initialEmail?: string;
  onCancel: () => void;
  onSuccess: () => void;
};

export function AddAdminModal({ initialEmail = '', onCancel, onSuccess }: Props) {
  const { register, handleSubmit, formState, setError } = useForm<FormData>({
    defaultValues: { email: initialEmail },
  });
  const addAdmin = useAddAdminMutation();
  const navigate = useNavigate();
  const location = useAppLocation();
  const { errors, isSubmitting } = formState;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await addAdmin.mutateAsync({ email: data.email });
      onSuccess();
    } catch (error: unknown) {
      if (error instanceof AuthenticationError && error.code === 'ERR_2FA_RENEWAL_REQUIRED') {
        const pendingAddAdminId = createPendingAddAdmin(data.email);
        const search = pendingAddAdminId ? `addAdmin=${pendingAddAdminId}` : 'addAdmin=true';

        void navigate('/auth/mfa/renew', { state: { from: { ...location, search } }, replace: true });
      } else if (error instanceof ValidationError) {
        handleServerFormError(error, ({ path, message }) => {
          switch (path) {
            case 'email':
              setError(path, { message }, { shouldFocus: true });
              return true;
            case undefined:
              setError('root.global', { message });
              return true;
            default:
              setError('root.global', { message });
              return false;
          }
        });
      } else {
        setError('root.global', { message: 'An unexpected error occurred' });
        trackError(error);
      }
    }
  });

  return (
    <Modal isOpen={true} onClose={onCancel}>
      <ModalHeader>Add admin</ModalHeader>
      <ModalBody>
        <form id={FORM_NAME} onSubmit={onSubmit}>
          {errors.root?.global?.message && <ErrorMessage>{errors.root.global.message}</ErrorMessage>}
          <Field label="Email address">
            <Input
              className="width-100"
              type="email"
              hasError={Boolean(errors.email)}
              placeholder="admin@example.com"
              {...register('email', { required: 'Email is required' })}
            />
            {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
          </Field>
        </form>
      </ModalBody>
      <ModalFooter>
        <Spacer>
          <Button variant="link" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" form={FORM_NAME} disabled={isSubmitting}>
            Add
          </Button>
        </Spacer>
      </ModalFooter>
    </Modal>
  );
}
